import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
	Img,
	Heading,
	Text,
	Header,
	DefaultSideBar,
	LineChart,
	Grafik,
	SelectBox,
} from "components";
import { dashboard, summary as reqSummary } from "services/auth";
import { ChartBar } from "components/Chart/Bar";
import { ChartDoughnut } from "components/Chart/Doughnut";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function DashboardPage() {
	const [data, setData] = useState(null);
	const [resume, setResume] = useState([]);
	const [chartData, setChartData] = useState([]);
	const [summary, setSummary] = useState([]);
	const [category, setCategory] = useState('pool');
	const [startDate, setStartDate] = useState();
	const [endDate, setEndDate] = useState();

	const fetchData = async () => {
		try {
			const dashboardData = await dashboard();
			setResume(dashboardData.data.data[0].dashboard_data);

			if (!startDate || !endDate) return;

			const start_date = `${ (startDate.getMonth() + 1).toString().padStart(2, '0') }-${ startDate.getFullYear() }`;
			const end_date = `${ (endDate.getMonth() + 1).toString().padStart(2, '0') }-${ endDate.getFullYear() }`;

			const dashboardData2 = await reqSummary(category, start_date, end_date);
			setChartData(dashboardData2.data.data ?? []);

			const dashboardData3 = await reqSummary('summary', start_date, end_date);
			setSummary(dashboardData3.data.data ?? []);

			// const koperasiData = dashboardData.data.dashboard_data.dashboard_arr.find(item => item.title === "Koperasi");
			// setData(koperasiData ? koperasiData.data : null);
		} catch (error) {
			console.error("Error fetching Dashboard data:", error);
		}
	};

	useEffect(() => {
		if (!startDate) {
			const date = new Date();
			date.setDate(1);
			setStartDate(date);
		}
		if (!endDate) {
			const date2 = new Date();
			date2.setDate(1);
			date2.setMonth(date2.getMonth() + 1);
			setEndDate(date2);
		}
	}, []);

	useEffect(() => {
		startDate && endDate && fetchData();
	}, [startDate, endDate, category]);

	const username = localStorage.getItem("username");

	const _getResumeProps = useCallback((label) => {
		if (!label) return "";
		let icon = "images/img_profile_2user.svg",
			link = "";
		const labelLower = label.toString().toLowerCase();
		if (labelLower.includes("fund manager")) {
			icon = "images/img_majesticons_users_two.svg";
			link = "/fund-manager";
		}
		if (labelLower.includes("reporting")) {
			icon = "images/img_icon_park_table_report.svg";
			link = "/report";
		}
		if (labelLower.includes("batch")) {
			icon = "images/img_material_symbol_orange_300.svg";
			link = "/batch";
		}
		if (labelLower.includes("redeem pool")) {
			icon = "images/img_redeem.svg";
			link = "/redeem";
		}
		if (labelLower.includes("pool")) {
			icon = "images/img_material_pool.svg";
			link = "/pool";
		}
		if (labelLower.includes("originator")) {
			icon = "images/img_majesticons_users.svg";
			link = "/originator";
		}
		return { icon, link };
	}, []);

	const _topItem = useCallback(
		(title, value) => {
			const { icon, link } = _getResumeProps(title);
			return (
				<Link
					to={ link }
					key={ title }
					title={ title }
					className="flex-1 flex h-[100px] justify-start items-center gap-3 px-2 bg-white-A700 shadow-2xl rounded-[16px]"
				>
					<div className="h-[48px] w-[48px] relative">
						<div className="justify-center h-[48px] w-full left-0 bottom-0 right-0 top-0 m-auto bg-gradient absolute rounded-[50%]" />
						<Img
							src={ icon }
							alt="profile2user"
							className="justify-center h-[24px] left-0 bottom-0 right-0 top-0 m-auto absolute"
						/>
					</div>
					<div className="flex flex-col items-start justify-start gap-2">
						<Text
							as="p"
							className="!text-gray-500_02 tracking-[-0.14px] !font-poppins"
						>
							{ title.replaceAll("Total ", "") }
						</Text>
						<div className="flex items-end gap-1">
							<Heading
								size="3xl"
								as="h2"
								className="!text-blue_gray-900_01 tracking-[-0.32px]"
							>
								{ value > 9999 ? `${ value }+` : value }
							</Heading>
							<Text
								as="p"
								className="!text-gray-500_02 tracking-[-0.14px] !font-poppins"
							>
								{ title.toString().toLowerCase().includes("originator") || title.toString().toLowerCase().includes("fund manager") ? 'Accounts' : 'Units' }
							</Text>
						</div>
					</div>
				</Link>
			);
		},
		[_getResumeProps]
	);

	const _topContent = useMemo(() => {
		return (
			<>
				<Heading
					size="4xl"
					as="h1"
					className="!text-blue_gray-600 !font-leaguespartan w-full mt-5 mx-5"
				>
					Hi, { username }
				</Heading>
				{/* <div className="grid grid-cols-6 items-center justify-around w-full mt-5 gap-5 mx-5"> */ }
				<div className="flex items-center justify-between w-full mt-5 gap-2 px-5 overflow-hidden pb-2">
					{ resume.map((r) => _topItem(r.name, r.value)) }
				</div>
			</>
		);
	}, [_topItem, username, resume]);

	const _chartContent = useMemo(() => {
		return (
			<>
				<div className="flex px-5 mt-5 gap-2 items-start">
					<div className="flex-1 bg-white-A700 shadow-2xl rounded-[16px]">
						<div className="p-3">
							<div className="flex items-center gap-4">
								<p className="flex-1">Summary</p>
								<SelectBox
									options={ [
										// { label: 'Summary', value: 'summary' },
										{ label: 'Pool', value: 'pool' },
										{ label: 'Asset', value: 'asset' },
										{ label: 'Batch', value: 'batch' },
										{ label: 'Redeem', value: 'redeem' },
									] }
									placeholder="Pool"
									indicator={
										<Img
											src="images/img_arrowdown_blue_gray_900_02.svg"
											alt="arrow_down"
										/>
									}
									className="w-52 gap-px border-gray-300 border-2 border-solid !rounded-[5px]"
									onChange={ (val) => setCategory(val.value) }
								/>
								<DatePicker
									dateFormat="dd/MM/yyyy"
									className="font-opensans !border-gray-300_01 !border-2 !border-solid !rounded-[5px] !p-2"
									selected={ startDate }
									onSelect={ setStartDate }
									onChange={ setStartDate }
								/>
								<DatePicker
									dateFormat="dd/MM/yyyy"
									className="font-opensans !border-gray-300_01 !border-2 !border-solid !rounded-[5px] !p-2"
									minDate={ startDate }
									selected={ endDate }
									onSelect={ setEndDate }
									onChange={ setEndDate }
								/>
							</div>
							<ChartBar data={ chartData } />
						</div>
					</div>
					<div className="flex-3 bg-white-A700 shadow-2xl rounded-[16px]">
						<div className="p-3">
							<p>Pool Summary</p>
							<ChartDoughnut data={ summary } />
						</div>
					</div>
				</div>
			</>
		);
	}, [summary]);

	return (
		<div className=" w-full relative bg-gray-100 pl-[81px] pt-[10vh] h-screen">
			<Header />
			<DefaultSideBar />
			{ _topContent }
			{ _chartContent }
			{ data && (
				<div className="flex flex-col items-start justify-start w-full mt-5 gap-[17px] px-10">
					<Heading
						size="4xl"
						as="h1"
						className="ml-3.5 !text-blue_gray-600 !font-leaguespartan"
					>
						Hi, { username }
					</Heading>
					<div className="grid grid-cols-2 w-full gap-x-20 gap-y-10 mt-10">
						{ data.map((data) => (
							<div
								key={ data.title }
								className="flex flex-row justify-start p-10 bg-white-A700 shadow-2xl rounded-[30px] w-full"
							>
								<div className="flex flex-row justify-start items-center w-full gap-[22px] px-10">
									<div className="h-[98px] w-[98px] relative">
										<div className="justify-center h-[98px] w-full left-0 bottom-0 right-0 top-0 m-auto bg-gradient absolute rounded-[50%]" />
										<Img
											src="images/img_profile_2user.svg"
											alt="profile2user"
											className="justify-center h-[49px] left-0 bottom-0 right-0 top-0 m-auto absolute"
										/>
									</div>
									<div className="flex flex-col items-start justify-start gap-2">
										<Text
											as="p"
											className="!text-gray-500_02 tracking-[-0.14px] !font-poppins"
										>
											{ data.title }
										</Text>
										<Heading
											size="3xl"
											as="h2"
											className="!text-blue_gray-900_01 tracking-[-0.32px]"
										>
											{ data.value }
										</Heading>
									</div>
								</div>
							</div>
						)) }
					</div>
				</div>
			) }

			{/* <div className="grid grid-cols-2 gap-10 w-full">
        <div className="flex flex-col items-start justify-start w-full gap-[21px] p-5 bg-white-A700 rounded-[10px]">
          <Text
            size="2xl"
            as="p"
            className="ml-[19px] !text-blue_gray-600 !font-dmsans"
          >
            Status
          </Text>
          <div className="flex flex-row justify-center w-[89%] mb-4 ml-[19px]">
            <div className="flex flex-col items-start justify-start w-full gap-[27px]">
              <div className="flex flex-row justify-start w-[45%] gap-5">
                <div className="flex flex-row justify-start items-center w-[45%] gap-1.5">
                  <Img
                    src="images/img_color.svg"
                    alt="color_one"
                    className="h-[12px]"
                  />
                  <Text as="p" className="!text-black-900 !font-dmsans">
                    Active{" "}
                  </Text>
                </div>
                <div className="flex flex-row justify-start items-center w-[45%] gap-1.5">
                  <Img
                    src="images/img_color_blue_gray_50.svg"
                    alt="color_three"
                    className="h-[12px]"
                  />
                  <Text as="p" className="!text-black-900 !font-dmsans">
                    Closed
                  </Text>
                </div>
              </div>
              <div className="flex flex-row justify-start items-end w-full gap-4">
                <LineChart />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col justify-between w-full gap-[21px] p-5 bg-white-A700 rounded-[10px]">
          <Text
            size="2xl"
            as="p"
            className="ml-5 !text-blue_gray-600 !font-dmsans"
          >
            Grafik
          </Text>
          <Grafik />
        </div>
      </div> */}
		</div>
	);
}
